/* eslint-disable react-refresh/only-export-components -- context module exports hooks + provider */
import type React from 'react'
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react'
import { useApiClient } from '../services/api/axiosClient'
import { fetchHospitals } from '../services/api/hospitalService'
import { useEncounterSession } from './encounterSessionContext'

export type Hospital = {
  id: string
  name: string
}

export type HospitalContextValue = {
  hospitals: Hospital[]
  setHospitals: (next: Hospital[]) => void
  selectedHospitalId: string
  setSelectedHospitalId: Dispatch<SetStateAction<string>>
  hospitalsLoading: boolean
  setHospitalsLoading: (next: boolean) => void
}

export const HospitalContext = createContext<HospitalContextValue | null>(null)

export function useHospitalContext(): HospitalContextValue {
  const value = useContext(HospitalContext)
  if (!value) {
    throw new Error('useHospitalContext must be used within HospitalProvider')
  }
  return value
}

/**
 * Fetches hospitals once (Auth0 token available). Mount inside HospitalProvider, below Auth0.
 */
export function HospitalListLoader() {
  const apiClient = useApiClient()
  const { sessionId } = useEncounterSession()
  const {
    setHospitals,
    setSelectedHospitalId,
    setHospitalsLoading,
  } = useHospitalContext()

  useEffect(() => {
    let isMounted = true

    const loadHospitals = async () => {
      try {
        setHospitalsLoading(true)

        let items: Hospital[]
        try {
          items = await fetchHospitals(apiClient, { sessionId })
        } catch {
          // First-request failures are often transient (cold gateway start, connection race).
          // Wait briefly, bail if the component was already unmounted, then retry once.
          await new Promise<void>((resolve) => setTimeout(resolve, 1500))
          if (!isMounted) return
          items = await fetchHospitals(apiClient, { sessionId })
        }

        if (!isMounted) return
        setHospitals(items.sort((a, b) => a.name.localeCompare(b.name)))
        // Functional updater so the check runs against the latest queued state,
        // not stale rendered state — avoids a race with fetchDirectPatient.
        if (items.length > 0) {
          setSelectedHospitalId((current) => current || items[0]!.id)
        }
      } catch (error) {
        console.error('Failed to load hospitals', error)
      } finally {
        if (isMounted) {
          setHospitalsLoading(false)
        }
      }
    }

    void loadHospitals()

    return () => {
      isMounted = false
    }
  }, [apiClient, sessionId, setHospitals, setHospitalsLoading, setSelectedHospitalId])

  return null
}

export function useHospitalProviderValue(): HospitalContextValue {
  const [hospitals, setHospitals] = useState<Hospital[]>([])
  const [selectedHospitalId, setSelectedHospitalId] = useState<string>('')
  const [hospitalsLoading, setHospitalsLoading] = useState(true)

  return useMemo<HospitalContextValue>(
    () => ({
      hospitals,
      setHospitals,
      selectedHospitalId,
      setSelectedHospitalId,
      hospitalsLoading,
      setHospitalsLoading,
    }),
    [hospitals, selectedHospitalId, hospitalsLoading],
  )
}

export function HospitalProvider({ children }: { children: React.ReactNode }) {
  const value = useHospitalProviderValue()

  return (
    <HospitalContext.Provider value={value}>
      {children}
    </HospitalContext.Provider>
  )
}
